import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    IconButton,
    Paper,
    Divider,
    Stack,
    CircularProgress,
    Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { useSessionClass } from '../../contexts/SessionClassContext';
import { useStudents } from '../../hooks/useStudents';
import { useTeacher } from '../../hooks/useTeacher';
import { useAttendance } from '../../hooks/useAttendance';
import { useAuth } from '../../hooks/useAuth';
import { useTenantHeader } from '../../hooks/useTenantHeader';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useHolidays } from '../../hooks/useHolidays';
import { useError } from '../../contexts/ErrorContext';
import { format, parseISO } from 'date-fns';

interface AttendanceSlideModalProps {
    open: boolean;
    onClose: () => void;
}

const AttendanceSlideModal: React.FC<AttendanceSlideModalProps> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { showError } = useError();
    const schoolId = user?.schoolId;
    const { selectedSession, selectedClass } = useSessionClass();

    const [step, setStep] = useState(0); // 0: Setup, 1 to N: Students, N+1: Summary
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [attendanceDate, setAttendanceDate] = useState<string>('');
    const [selectedTeacherId, setSelectedTeacherId] = useState<number | ''>('');
    const [studentAttendance, setStudentAttendance] = useState<Record<number, string>>({});
    const [lastNotifiedSelection, setLastNotifiedSelection] = useState<string>('');

    const { tenantLoading } = useTenantHeader();
    const { teachersQuery } = useTeacher(schoolId ?? undefined);
    const { useStudentsBySessionIdAndClassId } = useStudents(tenantLoading);
    const { bulkAttendanceMutation, attendanceStatusQuery, useAttendanceByDateQuery } = useAttendance();
    const { useholidaysQueryBySession } = useHolidays(selectedSession?.id);

    // Format date for the attendance check API (dd-MM-yyyy)
    const formattedAttendanceCheckDate = useMemo(() => {
        if (!attendanceDate) return '';
        try {
            return format(parseISO(attendanceDate), "dd-MM-yyyy");
        } catch (e) {
            return '';
        }
    }, [attendanceDate]);

    // Query to check if attendance exists for the selected date
    const attendanceCheckQuery = useAttendanceByDateQuery(
        selectedClass?.id,
        selectedSession?.id,
        formattedAttendanceCheckDate || undefined
    );

    // Watch for existing attendance and show notification
    useEffect(() => {
        const selectionKey = `${selectedClass?.id}-${attendanceDate}`;
        if (open && attendanceCheckQuery.data && attendanceCheckQuery.data.length > 0) {
            // Only show the error once for this selection
            if (lastNotifiedSelection !== selectionKey) {
                showError(`${t('attendance_already_exist_for_this_date')}`);
                setLastNotifiedSelection(selectionKey);
            }
        } else if (open && attendanceCheckQuery.data?.length === 0) {
            // If data becomes empty (date switched), we can clear the notified state
            setLastNotifiedSelection('');
        }
    }, [attendanceCheckQuery.data, open, t, showError, selectedClass?.id, attendanceDate, lastNotifiedSelection]);

    const studentsQuery = useStudentsBySessionIdAndClassId(
        selectedSession?.id,
        selectedClass?.id
    );

    const filteredStudents = useMemo(() => {
        if (!studentsQuery.data) return [];
        return studentsQuery.data.filter(s => s.status !== false)
            .sort((a, b) => (a.studentName || '').toLowerCase().localeCompare((b.studentName || '').toLowerCase()));
    }, [studentsQuery.data]);

    // Fetch all marked attendance dates to disable them
    const { getAttendanceQuery: allMarkedAttendanceQuery } = useAttendance({
        sessionId: selectedSession?.id || 0,
        classId: selectedClass?.id || 0,
        startDate: selectedSession?.startDate || undefined,
        endDate: selectedSession?.endDate || undefined
    });

    const markedDates = useMemo(() => {
        if (!allMarkedAttendanceQuery.data) return new Set<string>();
        const dates = new Set<string>();
        allMarkedAttendanceQuery.data.forEach(record => dates.add(record.attendanceDate));
        return dates;
    }, [allMarkedAttendanceQuery.data]);

    const isDateDisabled = (date: Date) => {
        if (!date || isNaN(date.getTime())) return false;
        if (!useholidaysQueryBySession.data) return false;
        const dateStrForHoliday = format(date, "dd-MM-yyyy");
        return useholidaysQueryBySession.data.some((holiday) => {
            const isDateMatch = holiday.dates.includes(dateStrForHoliday);
            if (!isDateMatch) return false;
            return holiday.forAllClasses || holiday.classIds?.includes(selectedClass?.id || 0);
        });
    };

    useEffect(() => {
        if (open) {
            setStep(0);
            setAttendanceDate('');
            // Default teacher selection based on logged-in user's userId
            if (user && teachersQuery.data) {
                const currentTeacher = teachersQuery.data.find(t => t.userId === user.id);
                if (currentTeacher) {
                    setSelectedTeacherId(currentTeacher.id);
                } else {
                    setSelectedTeacherId('');
                }
            } else {
                setSelectedTeacherId('');
            }
            setStudentAttendance({});
            setLastNotifiedSelection('');
        }
    }, [open, user, teachersQuery.data]);

    // Populate studentAttendance when existing data is fetched
    useEffect(() => {
        if (open && attendanceDate && attendanceCheckQuery.data && attendanceCheckQuery.data.length > 0) {
            const attendanceMap: Record<number, string> = {};
            attendanceCheckQuery.data.forEach(record => {
                attendanceMap[record.studentId] = record.statusCode;
            });
            setStudentAttendance(attendanceMap);
        } else if (open) {
            setStudentAttendance({});
        }
    }, [attendanceCheckQuery.data, open, attendanceDate]);



    const handleStatusSelect = (studentId: number, status: string) => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setStudentAttendance(prev => ({ ...prev, [studentId]: status }));
        // Auto-slide to next
        setTimeout(() => {
            setStep(prev => prev + 1);
            setIsTransitioning(false);
        }, 300);
    };

    const handleNext = () => setStep(prev => prev + 1);
    const handlePrev = () => setStep(prev => prev - 1);

    const summaryData = useMemo(() => {
        const total = filteredStudents.length;
        const presents = Object.values(studentAttendance).filter(s => s === 'PRESENT').length;
        const absents = Object.values(studentAttendance).filter(s => s === 'ABSENT').length;
        const leaves = Object.values(studentAttendance).filter(s => s === 'LEAVE').length;
        return { total, presents, absents, leaves };
    }, [filteredStudents, studentAttendance]);

    const handleSubmit = () => {
        if (Object.keys(studentAttendance).length < filteredStudents.length) {
            showError("Please Mark Attendance of all students.");
            return;
        }

        const payload = {
            staffId: Number(selectedTeacherId),
            classId: Number(selectedClass?.id),
            sessionId: Number(selectedSession?.id),
            attendanceDate: attendanceDate,
            students: Object.entries(studentAttendance).map(([id, code]) => {
                const status = attendanceStatusQuery.data?.find(s => s.statusCode.toUpperCase() === code);
                return {
                    studentId: Number(id),
                    statusId: status?.id || 0,
                    remarks: code
                };
            })
        };

        bulkAttendanceMutation.mutate(payload, {
            onSuccess: () => onClose(),
            onError: (err: any) => showError(err?.response?.data?.message || t('failed_to_submit_attendance'))
        });
    };

    const renderStepContent = () => {
        if (step === 0) {
            return (
                <Stack spacing={4} sx={{ mt: 2, p: 2 }}>
                    <Typography variant="h6" align="center" color="textSecondary">
                        Select Date and Teacher
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label={t('date')}
                            value={attendanceDate ? parseISO(attendanceDate) : null}
                            format="dd-MM-yyyy"
                            onChange={(date) => {
                                if (date && !isNaN(date.getTime())) {
                                    setAttendanceDate(format(date, "yyyy-MM-dd"));
                                } else {
                                    setAttendanceDate('');
                                }
                            }}
                            shouldDisableDate={isDateDisabled}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </LocalizationProvider>
                    <FormControl fullWidth>
                        <InputLabel>{t('teacher_name')}</InputLabel>
                        <Select
                            value={selectedTeacherId}
                            onChange={(e) => setSelectedTeacherId(e.target.value as number)}
                            label={t('teacher_name')}
                        >
                            {/* Only include current user as an fallback option if no teacher record matches their userId */}
                            {user && !teachersQuery.data?.some(t => t.userId === user.id) && (
                                <MenuItem key={`user-${user.id}`} value={user.id}>
                                    {user.username}
                                </MenuItem>
                            )}
                            {teachersQuery.data?.map((teacher) => (
                                <MenuItem key={teacher.id} value={teacher.id}>
                                    {teacher.firstName} {teacher.lastName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            );
        }

        if (step > 0 && step <= filteredStudents.length) {
            const student = filteredStudents[step - 1];
            const currentStatus = studentAttendance[student.id!];

            return (
                <Fade in={true} key={step}>
                    <Stack spacing={4} alignItems="center" sx={{ mt: 4, p: 2 }}>
                        <Typography variant="h4" fontWeight="bold" align="center">
                            {student.studentName}
                        </Typography>
                        <Typography variant="subtitle1" color="textSecondary">
                            {t('roll_number')}: {student.rollNumber || 'N/A'} | Student {step} of {filteredStudents.length}
                        </Typography>

                        <Stack direction="row" spacing={3} sx={{ mt: 4 }}>
                            <Button
                                variant={currentStatus === 'PRESENT' ? 'contained' : 'outlined'}
                                color="success"
                                size="large"
                                onClick={() => handleStatusSelect(student.id!, 'PRESENT')}
                                sx={{ py: 2, px: 4, borderRadius: 3, fontWeight: 'bold', fontSize: '1.2rem' }}
                            >
                                {t('present')}
                            </Button>
                            <Button
                                variant={currentStatus === 'ABSENT' ? 'contained' : 'outlined'}
                                color="error"
                                size="large"
                                onClick={() => handleStatusSelect(student.id!, 'ABSENT')}
                                sx={{ py: 2, px: 4, borderRadius: 3, fontWeight: 'bold', fontSize: '1.2rem' }}
                            >
                                {t('absent')}
                            </Button>
                            <Button
                                variant={currentStatus === 'LEAVE' ? 'contained' : 'outlined'}
                                color="warning"
                                size="large"
                                onClick={() => handleStatusSelect(student.id!, 'LEAVE')}
                                sx={{ py: 2, px: 4, borderRadius: 3, fontWeight: 'bold', fontSize: '1.2rem' }}
                            >
                                {t('leave')}
                            </Button>
                        </Stack>
                    </Stack>
                </Fade>
            );
        }

        if (step > filteredStudents.length) {
            return (
                <Fade in={true}>
                    <Stack spacing={3} alignItems="center" sx={{ mt: 2, p: 2 }}>
                        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80 }} />
                        <Typography variant="h5" fontWeight="bold">{t('attendance_summary')}</Typography>
                        <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2, width: '100%' }}>
                            <Stack spacing={2}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography>{t('total_students')}:</Typography>
                                    <Typography fontWeight="bold">{summaryData.total}</Typography>
                                </Stack>
                                <Divider />
                                <Stack direction="row" justifyContent="space-between" color="success.main">
                                    <Typography>{t('present')}:</Typography>
                                    <Typography fontWeight="bold">{summaryData.presents}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" color="error.main">
                                    <Typography>{t('absent')}:</Typography>
                                    <Typography fontWeight="bold">{summaryData.absents}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" color="warning.main">
                                    <Typography>{t('leave')}:</Typography>
                                    <Typography fontWeight="bold">{summaryData.leaves}</Typography>
                                </Stack>
                            </Stack>
                        </Paper>
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {summaryData.total > Object.keys(studentAttendance).length && "Some students are missing attendance!"}
                        </Typography>
                    </Stack>
                </Fade>
            );
        }
    };

    return (
        <Dialog open={open} onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                onClose();
            }
        }} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ borderBottom: '1px solid #eee', bgcolor: '#fcfcfc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">{t('one_by_one_attendance')}</Typography>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ maxHeight: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0 }}>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                    {renderStepContent()}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #eee', bgcolor: '#fcfcfc', justifyContent: 'space-between' }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handlePrev}
                    disabled={step === 0}
                    color="inherit"
                >
                    {t('previous')}
                </Button>

                {step > filteredStudents.length ? (
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        color="primary"
                        disabled={bulkAttendanceMutation.isLoading || Object.keys(studentAttendance).length < filteredStudents.length}
                        sx={{ px: 4, borderRadius: 2 }}
                    >
                        {bulkAttendanceMutation.isLoading ? <CircularProgress size={24} /> : "OK"}
                    </Button>
                ) : (
                    <Button
                        endIcon={<ArrowForwardIcon />}
                        onClick={handleNext}
                        disabled={step === 0 && (!selectedTeacherId || !attendanceDate || isDateDisabled(parseISO(attendanceDate)) || (attendanceCheckQuery.data && attendanceCheckQuery.data.length > 0))}
                        color="inherit"
                    >
                        {t('next')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AttendanceSlideModal;
